import type {
  ExtendedProductVariant,
  WovarCustomer,
  WovarProduct,
} from "@commercekit/api";
import { ProductStockAvailabilityEnum } from "@commercekit/api";
import { NotifyMeAction } from "@commercekit/core";
import type { HTMLComponent } from "@commercekit/react";
import type { ActionData } from "@commercekit/remix/index";
import {
  getFullExcessSaleText,
  useActionResult,
  useCart,
  useCheckoutSimple,
  useLocale,
  useLocalePath,
} from "@commercekit/remix/index";
import {
  fallbackMoney,
  formatMoneyWithCurrency,
  taxExemptedCustomer,
} from "@commercekit/shared";
import { GoogleReCaptchaCheckbox } from "@google-recaptcha/react";
import { Form, useActionData, useNavigation } from "@remix-run/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWvAddToCart, useWvNotifyStock } from "~/hooks/useDataLayer";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import type { ProductAndCategoryDocumentDataPaymentMethodsItem } from "~/lib/prismic";
import WvFormHiddenInputs from "../atoms/WvFormHiddenInputs";
import WvCartActionAlert from "../molecules/WvCartActionAlert";
import WvPaymentMethodsRow from "../molecules/WvPaymentOptionsRow";
import WvProductActionBar from "../molecules/WvProductActionBar";
import WvProductVariants from "./WvProductVariants";
import WvProductSwitcher from "../molecules/WvProductSwitcher";
import type { ProductSwitcher } from "~/lib/productSwitcher";
import WvProductShippingInfo from "./WvProductShippingInfo";
import { getDeliveryEstimate } from "~/lib/deliveryEstimate";
import { useUnitOfMeasure, SINGLE_CART_LIMIT } from "~/hooks/useUnitOfMeasure";

const WvProductDetailCard: HTMLComponent<{
  product: WovarProduct;
  onOpenCartPopup: () => void;
  paymentMethods: ProductAndCategoryDocumentDataPaymentMethodsItem[];
  customer?: WovarCustomer;
  captchaSiteKey: string;
  updateSelectedProductVariant: (
    selectedProductVariant: ExtendedProductVariant | undefined | any
  ) => void;
  updateSelectedUomAndQuantity: (uomPropertyAndQuantity: string) => void;
  productSwitcher?: ProductSwitcher | null;
}> = ({
  product,
  onOpenCartPopup: _onOpenCartPopup,
  paymentMethods,
  customer,
  updateSelectedProductVariant,
  updateSelectedUomAndQuantity,
  productSwitcher,
}) => {
  const { localeFullName } = useLocale();
  const localePath = useLocalePath();
  const { $pt } = usePrismicLabels();

  const { addToCart, loadingQueue, cartLoading } = useCart();
  const gtmAddToCart = useWvAddToCart();
  const gtmNotifyStock = useWvNotifyStock();

  const { checkoutSimple: checkout } = useCheckoutSimple();

  const { formAction } = useNavigation();
  const actionData = useActionData<ActionData<NotifyMeAction>>();
  const notifyMeLoading = useMemo(
    () => formAction === localePath(`/${product.slug}`),
    [formAction, localePath, product.slug]
  );
  const notifyMeReady = useMemo(
    () => !notifyMeLoading && !formAction && !!actionData,
    [notifyMeLoading, formAction, actionData]
  );
  const [productCount, setProductCount] = useState("1");
  const [showExcessAddToCart, setShowExcessAddToCart] = useState(false);
  const [showExcessStock, setShowExcessStock] = useState(false);
  const [showExcessSaleAddToCart, setShowExcessSaleAddToCart] = useState(false);
  const [showBottomFixedBar, setShowBottomFixedBar] = useState(false);
  const [isTopBarVisible, setIsTopBarVisible] = useState(false);
  const [isFixGreyCard, setIsFixGreyCard] = useState(false);
  const fixedBarRef = useRef<HTMLDivElement>(null);
  const primaryUOM = useMemo(() => {
    return product.unitsOfMeasure.find((v) => v.primary);
  }, [product.unitsOfMeasure]);

  const quantityInCart = useMemo(() => {
    return checkout?.lineItems
      ?.filter((v) => v?.product?.slug === product?.slug)
      .reduce((a, v) => {
        const uomQuantity = Number(
          product.unitsOfMeasure.find((x) => x.uomProperty === v.uomProperty)
            ?.quantity
        );
        return a + v?.quantity * uomQuantity;
      }, 0);
    //   ?.quantity || 0;
  }, [checkout?.lineItems, product?.slug, product.unitsOfMeasure]);

  const addProductToCart = useCallback(() => {
    setShowExcessAddToCart(false);
    setShowExcessStock(false);
    setShowExcessSaleAddToCart(false);
    const quantity = Number(productCount) > 0 ? Number(productCount) : 1;
    setProductCount(`${quantity}`);
    const proceedToAdd = () => {
      addToCart({ ...product, quantity });
      gtmAddToCart(product, quantity);
      updateSelectedUomAndQuantity(`${primaryUOM?.uomProperty}_${quantity}`);
    };

    const actualQuantity = (quantityInCart ?? 0) + quantity;

    if (actualQuantity > SINGLE_CART_LIMIT) {
      actualQuantity > product.stockLevel ? setShowExcessStock(true) : setShowExcessAddToCart(true);
      return;
    }

    if (!product.availableForPurchase && product.stockLevel > 0) {
      if (actualQuantity <= Math.min(product.stockLevel, SINGLE_CART_LIMIT))
        proceedToAdd();
      else {
        if (
          actualQuantity > product.stockLevel &&
          product.stockLevel < SINGLE_CART_LIMIT
        )
          setShowExcessStock(true);
        else setShowExcessAddToCart(true);
      }
    } else if (
      primaryUOM?.availability.canAddToCart &&
      product.availableForPurchase
    ) {
      if (actualQuantity > product.stockLevel) {
        if (product.stockLevel < SINGLE_CART_LIMIT) setShowExcessStock(true);
        else setShowExcessSaleAddToCart(true);
      }
      proceedToAdd();
    } else proceedToAdd();
  }, [
    addToCart,
    gtmAddToCart,
    product,
    productCount,
    primaryUOM,
    quantityInCart,
    updateSelectedUomAndQuantity,
  ]);

  const isAddingToCart = useMemo(() => {
    return (
      (loadingQueue?.find((i) => i?.id === product?.id) && cartLoading) || false
    );
  }, [loadingQueue, cartLoading, product]);

  const handleScroll = () => {
    const is_top_bar_visible = fixedBarRef.current
      ? fixedBarRef.current?.getBoundingClientRect()?.top > 65
      : false;

    setIsTopBarVisible(is_top_bar_visible);
    const show_bottom_fixed_bar =
      window.innerWidth <= 992 && !is_top_bar_visible;

    setShowBottomFixedBar(show_bottom_fixed_bar);

    fixGreyCard();
  };
  const handleResize = () => {
    if (window.innerWidth > 992) setShowBottomFixedBar(false);
    fixGreyCard();
  };

  const fixGreyCard = useCallback(() => {
    const productCardWrapper = document
      .getElementsByClassName("product-details-wrapper")[0]
      .getBoundingClientRect();

    const productCardWrapperTop = productCardWrapper.top;
    const productCardWrapperWidth = productCardWrapper.width;

    const greyCard = document.getElementsByClassName(
      "product-details-wrapper__payment-details--alt"
    )[0];

    const greyCardArea = greyCard.getBoundingClientRect();

    const headerArea = document
      .getElementsByTagName("header")[0]
      .getBoundingClientRect();

    const headerHeight = headerArea.height;

    const productInfoArea = document
      .getElementsByClassName("product-details-wrapper__description")[0]
      .getBoundingClientRect();

    const canFixGreyCard = greyCardArea.top - headerHeight <= 12.5;

    setIsFixGreyCard(canFixGreyCard);
    greyCard.style.width = `${
      productCardWrapperWidth -
      productInfoArea.width -
      productCardWrapperWidth * 0.02
    }px`;
    if (productCardWrapperTop >= headerHeight) {
      greyCard.style.position = "initial";
      greyCard.style.right = "initial";
      greyCard.style.top = "initial";
    } else {
      let top: number;
      if (productInfoArea.bottom <= greyCardArea.height + 12)
        top = productInfoArea.bottom - greyCardArea.height;
      else if (headerArea.top + headerHeight <= 0) top = 12;
      else top = headerHeight + 12 + (headerArea.top < 0 ? headerArea.top : 0);

      greyCard.style.position = "fixed";
      greyCard.style.right = `${productInfoArea.left}px`;
      greyCard.style.top = `${top}px`;
    }
  }, [isFixGreyCard, isTopBarVisible]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getUnitOfMeasure = useUnitOfMeasure();

  const notifyMeEmailRef = useRef("");

  useEffect(() => {
    if (notifyMeReady && !actionData?.errors?.length && notifyMeEmailRef.current) {
      gtmNotifyStock(product, notifyMeEmailRef.current);
    }
  }, [notifyMeReady, actionData, gtmNotifyStock, product]);

  const { transition } = useActionResult({
    action: `localePath/${product.slug}`,
    _action: NotifyMeAction.NOTIFY_ME,
  });
  const [showCaptcha, setShowCaptcha] = useState(false);
  const handleRecaptchaChange = useCallback((value: string | null) => {
    document
      ?.getElementById("recaptchaInput")
      ?.setAttribute("value", value ?? "");

    setTimeout(() => {
      setShowCaptcha(false);
    }, 1000);
    setTimeout(() => {
      document.getElementById("notifyEmailSubmit")?.click();
    }, 1200);
  }, []);

  const handleEmailChange = (email: string) => {
    document.getElementById("notifyEmail")?.setAttribute("value", email);
    notifyMeEmailRef.current = email;
  };

  const notB2B = useMemo(
    () => !customer || customer.type === "B2C",
    [customer]
  );

  const isB2B15 = useMemo(
    () => customer && customer.type.includes("B2B15"),
    [customer]
  );

  return (
    <div className="mb-4 pdp__order pdp__order--product-page">
      <div className="px-3 py-3 mb-3 pdp__order-inner px-xxl-4">
        {showExcessAddToCart && (
          <WvCartActionAlert
            message={$pt("product_excess_add_to_cart_message")}
            closeAlert={() => setShowExcessAddToCart(false)}
          />
        )}
        {showExcessStock && (
          <WvCartActionAlert
            message={$pt("product_excess_stock")}
            closeAlert={() => setShowExcessStock(false)}
          />
        )}
        {showExcessSaleAddToCart && (
          <WvCartActionAlert
            message={getFullExcessSaleText(
              product,
              $pt("product_excess_sale_cart_message")
            )}
            closeAlert={() => setShowExcessSaleAddToCart(false)}
          />
        )}
        {productSwitcher && (
          <WvProductSwitcher switcher={productSwitcher} customer={customer} />
        )}
        {(product?.variants && product?.variants?.length > 0) ||
        (product?.unitsOfMeasure && product?.unitsOfMeasure?.length > 1) ? (
          <WvProductVariants
            product={product}
            lineItems={checkout?.lineItems}
            quantityInCart={quantityInCart}
            customer={customer}
            setShowExcessAddToCart={setShowExcessAddToCart}
            setShowExcessStock={setShowExcessStock}
            setShowExcessSaleAddToCart={setShowExcessSaleAddToCart}
            updateSelectedProductVariant={(variant) => {
              updateSelectedProductVariant(variant);
            }}
            updateSelectedUomAndQuantity={updateSelectedUomAndQuantity}
          />
        ) : (
          <>
            <div className="position-relative">
              {isB2B15 ? (
                <div className="pdp__order-price price-mini">
                  <span className="text dark">{$pt("advice_price_label")}</span>
                  <span className="font-weight-bold">
                    <span
                      data-price={product?.recommendedPriceExclTax?.value}
                      className="mx-1"
                    >
                      {formatMoneyWithCurrency(
                        localeFullName,
                        product?.recommendedPriceExclTax?.currencyCode
                          ? product?.recommendedPriceExclTax
                          : fallbackMoney()
                      )}
                    </span>
                  </span>
                  <span className="mt-1 text dark">
                    {$pt("product_label_tax_exempted_price")}
                  </span>
                  <div className="mt-3 mb-1 text dark font-weight-bold price-mini">
                    {$pt("your_price_label")}:
                  </div>
                </div>
              ) : null}
              {product?.salePriceInclTax ? (
                <div className="pdp__order-price">
                  <div className="price d-flex">
                    {notB2B ? (
                      <>
                        <div
                          className="mr-2 d-inline-bloc price price--old"
                          data-price={product?.priceInclTax?.value}
                        >
                          {formatMoneyWithCurrency(
                            localeFullName,
                            product?.priceInclTax?.currencyCode
                              ? product?.priceInclTax
                              : fallbackMoney()
                          )}
                        </div>
                        <span
                          className="d-inline"
                          data-price={product?.salePriceInclTax?.value}
                        >
                          {formatMoneyWithCurrency(
                            localeFullName,
                            product.salePriceInclTax?.currencyCode
                              ? product?.salePriceInclTax
                              : fallbackMoney()
                          )}
                        </span>
                      </>
                    ) : (
                      <>
                        <div
                          className="mr-2 d-inline-bloc price price--old"
                          data-price={product?.priceExclTax?.value}
                        >
                          {formatMoneyWithCurrency(
                            localeFullName,
                            product?.priceExclTax?.currencyCode
                              ? product?.priceExclTax
                              : fallbackMoney()
                          )}
                        </div>
                        <span
                          className="d-inline"
                          data-price={product?.salePriceExclTax?.value}
                        >
                          {formatMoneyWithCurrency(
                            localeFullName,
                            product.salePriceExclTax?.currencyCode
                              ? product?.salePriceExclTax
                              : fallbackMoney()
                          )}
                        </span>
                      </>
                    )}
                  </div>
                  {!taxExemptedCustomer(customer?.type) ? (
                    <div className="mt-1 price price--vat">
                      <span
                        className=""
                        data-price={product?.salePriceExclTax?.value}
                      >
                        {notB2B
                          ? formatMoneyWithCurrency(
                              localeFullName,
                              product?.salePriceExclTax?.currencyCode
                                ? product?.salePriceExclTax
                                : fallbackMoney()
                            )
                          : null}
                      </span>{" "}
                      <span>{$pt("product_label_excluding_tax")}</span>
                    </div>
                  ) : (
                    <div className="mt-1 price price--vat">
                      {$pt("product_label_tax_exempted_price")}
                    </div>
                  )}
                </div>
              ) : (
                <div className="pdp__order-price">
                  <div className="price">
                    {notB2B ? (
                      <span
                        className="d-inline"
                        data-price={product?.priceInclTax?.value}
                      >
                        {formatMoneyWithCurrency(
                          localeFullName,
                          product?.priceInclTax?.currencyCode
                            ? product?.priceInclTax
                            : fallbackMoney()
                        )}
                      </span>
                    ) : (
                      <span
                        className="d-inline"
                        data-price={product?.priceExclTax?.value}
                      >
                        {formatMoneyWithCurrency(
                          localeFullName,
                          product?.priceExclTax?.currencyCode
                            ? product?.priceExclTax
                            : fallbackMoney()
                        )}
                      </span>
                    )}
                  </div>
                  {!taxExemptedCustomer(customer?.type) ? (
                    <div className="mt-1 price price--vat">
                      <span
                        className=""
                        data-price={product?.priceExclTax?.value}
                      >
                        {notB2B
                          ? formatMoneyWithCurrency(
                              localeFullName,
                              product?.priceExclTax?.currencyCode
                                ? product?.priceExclTax
                                : fallbackMoney()
                            )
                          : null}
                      </span>{" "}
                      <span>{$pt("product_label_excluding_tax")}</span>
                    </div>
                  ) : (
                    <div className="mt-1 price price--vat">
                      {$pt("product_label_tax_exempted_price")}
                    </div>
                  )}
                </div>
              )}

              <div className="pdp__order-discount position-right position-top position-absolute">
                {isAddingToCart && (
                  <i className="zmdi-hc-spin large" data-icon-after=""></i>
                )}
              </div>
            </div>

            <div className="my-3 pdp__order-addtocart">
              {primaryUOM?.availability?.canAddToCart && (
                <div className="my-3 pdp__order-addtocart">
                  <div className="product-form product-submit-form">
                    <div className="mb-3 row">
                      <div className="col-auto count">
                        <input
                          type="number"
                          name="count"
                          className="form-control"
                          value={productCount}
                          onChange={(e) => {
                            setProductCount(e.target.value);
                          }}
                          onKeyDown={({ key }) => {
                            if (key === "Enter") addProductToCart();
                          }}
                          min="1"
                          step="1"
                        />
                      </div>

                      <div className="px-0 d-flex col col-lg-2 col-xxl-2 pdp__order-label align-items-center">
                        <span className="pr-1">
                          {" "}
                          <svg
                            width="18"
                            height="5"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M14.829 2.443c-.615.051-1.231.086-1.84.117a52.362 52.362 0 01-4.144-.043c-1.375-.066-2.729-.242-4.094-.411a62.302 62.302 0 01-1.98-.272 192.58 192.58 0 01-.358-.073c.276-.136.556-.249.824-.376.57-.262.16-1.222-.424-1.014-.797.289-1.604.57-2.38.902-.336.143-.63.585-.37.948.585.8 1.164 1.603 1.748 2.407.599.83 1.759-.093 1.105-.898-.251-.306-.502-.618-.757-.93.122.03.232.063.348.092.682.197 1.376.387 2.077.529 1.351.27 2.75.414 4.133.515a32.81 32.81 0 004.292.03 39.79 39.79 0 002.15-.173c.613-.069 1.25-.06 1.782-.403.414-.262.302-.866-.137-1.003-.618-.193-1.334.005-1.975.056"
                              fill="#626262"
                              fillRule="evenodd"
                            ></path>
                          </svg>
                        </span>
                        <span>
                          {getUnitOfMeasure(
                            product.unitsOfMeasure.find((v) => v.primary)
                              ?.uomProperty ?? "",
                            parseInt(productCount) > 1
                          )}
                        </span>
                      </div>

                      <div
                        className="mt-3 col-12 col-lg mt-lg-0 pl-lg-0 pl-xl-3"
                        ref={fixedBarRef}
                      >
                        <WvProductActionBar onAddToCart={addProductToCart} />
                      </div>
                    </div>
                  </div>

                  <div
                    className={`pdp__stickybar d-lg-none ${
                      showBottomFixedBar ? "show" : ""
                    }`}
                  >
                    <div className="product-form product-submit-form">
                      <div className="flex-wrap mb-2 d-flex">
                        <div className="mt-2 col-12">
                          <WvProductActionBar onAddToCart={addProductToCart} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pdp__order-delivery-stock">
                <div className="flex-wrap d-flex flex-column">
                  <div className="px-2 b-2 col-12 col-lg-auto mb-md-0 pb-2">
                    {primaryUOM?.availability.stockAvailabilityMessage ===
                      ProductStockAvailabilityEnum.IN_STOCK ||
                    primaryUOM?.availability.stockAvailabilityMessage ===
                      ProductStockAvailabilityEnum.SOME_UNITS_AVAILABLE ? (
                      <span data-icon-before="">
                        {primaryUOM?.availability.stockAvailabilityMessage ===
                        ProductStockAvailabilityEnum.IN_STOCK ? (
                          <>{$pt("product_label_in_stock")}</>
                        ) : null}
                        {primaryUOM?.availability.stockAvailabilityMessage ===
                        ProductStockAvailabilityEnum.SOME_UNITS_AVAILABLE ? (
                          <>
                            {product?.stockLevel}{" "}
                            {getUnitOfMeasure(
                              product.unitsOfMeasure.find((v) => v.primary)
                                ?.uomProperty ?? "",
                              product.stockLevel > 1
                            ).toLowerCase()}{" "}
                            {$pt("product_label_available")}
                          </>
                        ) : null}
                      </span>
                    ) : (
                      <span className="not-in-stock" data-icon-before="">
                        {primaryUOM?.availability.stockAvailabilityMessage ===
                        ProductStockAvailabilityEnum.OUT_OF_STOCK ? (
                          <>{$pt("product_label_out_of_stock")}</>
                        ) : null}
                        {primaryUOM?.availability?.stockAvailabilityMessage ===
                        ProductStockAvailabilityEnum.IMPOSSIBLE_TO_ORDER ? (
                          <>{$pt("product_label_impossible_to_order")}</>
                        ) : null}
                      </span>
                    )}
                  </div>
                  {primaryUOM?.availability.stockAvailabilityMessage !==
                  ProductStockAvailabilityEnum.IMPOSSIBLE_TO_ORDER ? (
                    <div className="px-2 col-12 col-lg-auto">
                      <span
                        className="truck"
                        {...(product.stockLevel > 0 && {
                          "data-icon-after": "",
                        })}
                      >
                        {getDeliveryEstimate({
                          $pt,
                          deliveryEstimate:
                            primaryUOM?.availability?.deliveryEstimateMessage,
                          deliveryDate: primaryUOM?.availability?.deliveryDate,
                          workingDays: primaryUOM?.availability?.workingDays,
                          defaultShipping: primaryUOM?.defaultShipping,
                        })}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
              <WvProductShippingInfo product={product} notB2B={notB2B} />
            </div>
          </>
        )}
        {localeFullName != "nl-nl" && (
          <div className="mt-4 mb-3 pdp__order-payment">
            <WvPaymentMethodsRow paymentMethods={paymentMethods} />
          </div>
        )}

        <div className="row ml-lg-n2">
          <div className="col pdp__order-usps d-flex">
            <div className="col pl-lg-0">
              <div className="d-flex no-gutters">
                <div
                  className="col-auto mr-2 d-flex align-items-center"
                  data-icon-before=""
                ></div>
                <div className="col">
                  <div className="mb-0 heading sm strong sans-serif">
                    {$pt("wovar_over_hardware_stores")}
                  </div>
                  <div className="mb-0 heading sm sans-serif">
                    {$pt("wovar_quality_deals")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {primaryUOM?.availability.canLeaveEmailNoBackOrder && (
          <div>
            <Form
              method="post"
              id="notifyMeForm"
              action={localePath(`/${product.slug}`)}
            >
              <WvFormHiddenInputs
                data={{
                  _action: NotifyMeAction.NOTIFY_ME,
                  sku: product.sku,
                }}
              />
              <div className="mt-3 mb-1 heading sm strong sans-serif">
                {$pt("notify_email_label_text")}
              </div>
              <div className="form-row flex-nowrap w-100">
                <input
                  type="text"
                  id="notifyEmail"
                  name="email"
                  disabled={notifyMeLoading || showCaptcha}
                  className="form-control w-100"
                  placeholder={$pt("notify_email_placeholder_text")}
                  onChange={(e) => handleEmailChange(e.target.value)}
                />
                <input
                  id="recaptchaInput"
                  type="hidden"
                  name="recaptchaValue"
                />
                <div className="col-auto pl-0 pr-0 ml-n1 mr-n1 mr-lg-0">
                  <button type="submit" id="notifyEmailSubmit" hidden></button>
                  <button
                    type="button"
                    disabled={notifyMeLoading || showCaptcha}
                    className="btn form-control"
                    aria-label="notify me"
                    onClick={() => setShowCaptcha(true)}
                  >
                    {transition.state !== "idle"
                      ? $pt("form_action_loading")
                      : $pt("notify_email_button_text")}
                  </button>
                </div>
              </div>

              {showCaptcha && (
                <div className="mt-3">
                    <GoogleReCaptchaCheckbox onChange={handleRecaptchaChange} />
                </div>
              )}
            </Form>
            {notifyMeReady && (
              <>
                {actionData?.errors?.length ? (
                  <p className="text-danger">{actionData?.errors[0]}</p>
                ) : (
                  <p className="text-success">
                    {$pt("notify_email_success_text", {
                      email: actionData?.value?.email,
                      sku: actionData?.value?.sku,
                    })}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WvProductDetailCard;
